import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { FileSignature } from "lucide-react";
import { toast } from "sonner";

const FIELD = { ri: "signed_ri_at", charte: "signed_charte_at" };

function requiredDocs(user) {
  const req = [];
  if (user?.role === "parent") req.push("ri");
  if (user?.role === "teacher" || user?.is_teacher) req.push("charte");
  return req.filter((k) => !user?.[FIELD[k]]);
}

export default function SignatureGate() {
  const { user, setUser } = useAuth();
  const pending = requiredDocs(user);
  const kind = pending[0];
  const [doc, setDoc] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!kind || user?.must_change_password) { setDoc(null); return; }
    setAccepted(false);
    setName(user?.name || "");
    api.get(`/documents/${kind}`).then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [kind, user?.must_change_password]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!kind || user?.must_change_password || !doc) return null;

  const sign = async () => {
    if (!accepted) { toast.error("Veuillez cocher la case d'acceptation"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${kind}/sign`, { name });
      toast.success(`${doc.title} signé`);
      setUser({ ...user, [FIELD[kind]]: data.signed_at });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid={`signature-dialog-${kind}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[#1E3A8A]" /> {doc.title}
          </DialogTitle>
          <DialogDescription>
            Signature obligatoire pour accéder à votre espace{pending.length > 1 ? ` (${pending.length} documents à signer)` : ""}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto border border-[#EAE6DF] rounded-xl p-4 bg-[#FCFBF8] text-sm text-[#1A2547] whitespace-pre-wrap min-h-[200px]" data-testid="signature-doc-content">
          {doc.content}
        </div>
        <div className="space-y-3 pt-1">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="signature-accept-checkbox" className="mt-0.5" />
            <span>J'ai lu et j'accepte l'intégralité de ce document.</span>
          </label>
          <div>
            <Label className="text-xs">Signature (votre nom complet)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom NOM" className="rounded-xl mt-1" data-testid="signature-name-input" />
          </div>
          <Button onClick={sign} disabled={busy || !accepted || name.trim().length < 3} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="signature-submit">
            {busy ? "Signature…" : "Signer le document"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
